import { fetcher } from "../../../lib/api-fetcher";

import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { usePageTitle } from '../../../lib/hooks';
import { PageHeader } from '../../../components/layout/PageHeader';
import { PageSection } from '../../../components/layout/PageSection';
import { StatCard } from '../../../components/layout/StatCard';
import { Button } from '../../../components/ui/Button';
import { defineListPage } from '@repo/list';
import { DataTable, createUrlAdapter } from '@repo/web-list';
import { useListPage } from '@repo/list';
import { vaultHooks, type VaultHistoryItem } from '../../../features/accounting/network/vault.api';
import { AcceptHandoverModal } from '../../../features/accounting/components/RecordHandoverModal';
import { RecordDepositModal } from '../../../features/accounting/components/RecordDepositModal';
import { StatusBadge } from '../../../components/ui/StatusBadge';
import { Money } from '../../../components/ui/Money';
import { useCallback, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';

export const Route = createFileRoute('/_auth/finance/cash-vault')({
  component: CashVaultPage,
});

const vaultConfig = defineListPage<VaultHistoryItem>({
  variant: 'data-table',
  resource: 'vault-history',
  columns: [
    { id: 'id', header: 'ID', accessor: 'id' as const, renderType: 'code' },
    { id: 'createdAt', header: 'Date', accessor: 'createdAt' as const, renderType: 'datetime' },
    { id: 'type', header: 'Type', accessor: 'type' as const, renderType: 'text' },
    { id: 'amountPaise', header: 'Amount', accessor: 'amountPaise' as const, renderType: 'money', numeric: true },
    { id: 'notes', header: 'Notes', accessor: 'notes' as const, renderType: 'text' },
  ],
  defaultPageSize: 20,
});

function CashVaultPage() {
  usePageTitle('Cash Vault');
  const navigate = useNavigate();
  const search = Route.useSearch();
  const urlAdapter = useMemo(() => createUrlAdapter(search, navigate as any), [search, navigate]);

  const fetchFn = useCallback(async (params: Record<string, string | undefined>) => {
    const qs = Object.entries(params)
      .filter(([, v]) => v !== undefined)
      .map(([k, v]) => `${k}=${encodeURIComponent(v!)}`)
      .join('&');
    return fetcher.get<{ data: VaultHistoryItem[]; meta: { total: number; page: number; limit: number } }>(
      `/staff/v1/vault/history?${qs}`,
    );
  }, []);

  const list = useListPage(vaultConfig, fetchFn, urlAdapter);

  const { data: balanceData } = vaultHooks.useBalance();
  const [handoverOpen, setHandoverOpen] = React.useState(false);
  const [depositOpen, setDepositOpen] = React.useState(false);
  const balancePaise = balanceData?.balancePaise || 0;

  const todayHandoverPaise = (list.data ?? [])
    .filter((item: VaultHistoryItem) => item.type === 'HANDOVER')
    .reduce((sum: number, item: VaultHistoryItem) => sum + item.amountPaise, 0);

  return (
    <div className="space-y-6">
      <PageHeader 
        title="Cash Vault Management" 
        subtitle="Track branch cash vault balance, record bank deposits, and monitor agent cash handovers."
        action={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setHandoverOpen(true)}>
              Accept Handover
            </Button>
            <Button variant="primary" onClick={() => setDepositOpen(true)}>
              Record Bank Deposit
            </Button>
          </div>
        }
      />

      <PageSection>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <StatCard 
            label="Current Vault Balance" 
            value={<Money paise={balancePaise} />}
          />
          <StatCard 
            label="Agent Handovers (Today)" 
            value={<Money paise={todayHandoverPaise} />}
          />
        </div>
      </PageSection>

      <PageSection>
        <h3 className="text-lg font-medium text-text-primary mb-4">Transaction History</h3>
        <DataTable config={vaultConfig} list={list} />
      </PageSection>

      <AcceptHandoverModal 
        open={handoverOpen} 
        onClose={() => setHandoverOpen(false)} 
      />

      <RecordDepositModal 
        open={depositOpen} 
        onClose={() => setDepositOpen(false)} 
        maxAmountPaise={balancePaise}
      />
    </div>
  );
}
